import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ClockIcon,
    UsersIcon,
    SparklesIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import QuickReorderDashboard from '../components/QuickReorderDashboard';
import ScheduledOrdersManager from '../components/ScheduledOrdersManager';
import GroupOrdersManager from '../components/GroupOrdersManager';
import { showSuccessNotification } from '../components/NotificationManager';
import { jwtDecode } from 'jwt-decode';

const AdvancedOrders = () => {
    const [activeTab, setActiveTab] = useState('quick-reorder');
    const [userToken, setUserToken] = useState(null);
    const [userData, setUserData] = useState(null);
    const [selectedRestaurant, setSelectedRestaurant] = useState(null);
    const [restaurants, setRestaurants] = useState([]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setUserToken(token);
            try {
                const decoded = jwtDecode(token);
                setUserData(decoded);
            } catch (error) {
                console.error('Error decoding token:', error);
            }
        }
    }, []);

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await fetch('https://mongobyte-hc73.onrender.com/api/v1/restaurants');
                const data = await response.json();
                if (data.success && Array.isArray(data.data)) {
                    setRestaurants(data.data);
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error);
            }
        };
        fetchRestaurants();
    }, []);

    const tabs = [
        {
            id: 'quick-reorder',
            name: 'Quick Reorder',
            icon: HeartSolid,
            description: 'Instantly reorder your favorites',
            color: 'pepperoni'
        },
        {
            id: 'scheduled',
            name: 'Scheduled Orders',
            icon: ClockIcon,
            description: 'Schedule orders for later',
            color: 'cheese'
        },
        {
            id: 'group',
            name: 'Group Orders',
            icon: UsersIcon,
            description: 'Order together with friends',
            color: 'secondary-600'
        }
    ];

    const handleOrderSuccess = (order) => {
        showSuccessNotification(`Order #${order.customId} placed successfully!`);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-primary-50 py-6 px-4 pt-16 md:pt-24 pb-24 md:pb-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <SparklesIcon className="text-cheese text-3xl" />
                        <h1 className="text-3xl md:text-4xl font-bold text-crust font-display">
                            Advanced Orders
                        </h1>
                        <SparklesIcon className="text-cheese text-3xl" />
                    </div>
                    <p className="text-olive-light font-sans text-lg">
                        Manage your quick reorders, scheduled orders, and group orders
                    </p>
                </motion.div>

                {/* Tab Navigation */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-accentwhite rounded-2xl shadow-card border border-secondary-200 mb-8 overflow-hidden"
                >
                    <div className="flex flex-col md:flex-row">
                        {tabs.map((tab, index) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <motion.button
                                    key={tab.id}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex-1 p-6 text-left transition-all duration-300 relative ${isActive
                                        ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust'
                                        : 'text-olive-light hover:text-crust hover:bg-secondary-50'
                                        }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`p-3 rounded-xl ${isActive
                                            ? 'bg-accentwhite/20'
                                            : 'bg-secondary-100'
                                            }`}>
                                            <Icon className={`w-6 h-6 ${isActive
                                                ? 'text-crust'
                                                : 'text-pepperoni'
                                                }`} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <h3 className={`text-lg font-bold font-display ${isActive ? 'text-crust' : ''
                                                    }`}>
                                                    {tab.name}
                                                </h3>
                                            </div>
                                            <p className={`text-sm font-sans ${isActive
                                                ? 'text-crust/80'
                                                : 'text-olive-light'
                                                }`}>
                                                {tab.description}
                                            </p>
                                        </div>
                                    </div>

                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTab"
                                            className="absolute bottom-0 left-0 right-0 h-1 bg-pepperoni"
                                        />
                                    )}
                                </motion.button>
                            );
                        })}
                    </div>
                </motion.div>

                {/* Tab Content */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        {activeTab === 'quick-reorder' && (
                            <div className="space-y-6">
                                {/* Quick Reorder Dashboard */}
                                <QuickReorderDashboard
                                    userToken={userToken}
                                    onOrderSuccess={handleOrderSuccess}
                                />
                            </div>
                        )}

                        {activeTab === 'scheduled' && (
                            <ScheduledOrdersManager 
                                onOrderSuccess={handleOrderSuccess}
                            />
                        )}

                        {activeTab === 'group' && (
                            <GroupOrdersManager 
                                userToken={userToken}
                                userData={userData}
                                restaurants={restaurants}
                                selectedRestaurant={selectedRestaurant}
                                onRestaurantSelect={setSelectedRestaurant}
                                onOrderSuccess={handleOrderSuccess}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default AdvancedOrders;